import { useEffect, useRef } from 'react';
import type { ComparisonItem } from '../components/Topbar';
import type { GrowthDive, GrowthFacet, GrowthSubview } from '../data/growthTypes';
import {
  compareItemsFromFilters,
  opsStateFromView,
  replaceOpsUrl,
  type HotelTab,
} from '../lib/opsUrlState';
import {
  isOrgFilter,
  isOutletFilter,
  isVenuesFilter,
  scopeGroupIdFromFilter,
  venueIdsFromFilter,
} from '../lib/scopeFilter';
import type {
  FleetRestaurant,
  PerfSettings,
  ReservationSourceId,
  ReservationStatusGroup,
  ScopeGroupSummary,
  Outlet,
} from '../types';

export type OpsUrlSyncState = {
  mode: 'live' | 'performance' | 'settings' | 'growth' | 'horizon' | 'hotel' | 'assistant';
  regionFilter: string | null;
  /** The venues the venue filter shows (written as `sv` for a group or "All venues of"). */
  scopeVenueIds?: string[];
  compareItems: ComparisonItem[];
  source: ReservationSourceId[];
  status: ReservationStatusGroup[];
  shifts: string[];
  perfSettings: PerfSettings;
  drillKey: string | null;
  perfDrill: { metricKey: string; outletId: string | null } | null;
  focusOutlet: Outlet | null;
  northStarDrill: boolean;
  growthView?: GrowthSubview;
  growthDive?: GrowthDive | null;
  growthFacet?: GrowthFacet | null;
  growthChannel?: string | null;
  growthModel?: string | null;
  growthDemo?: boolean;
  horizonDate?: string | null;
  horizonOutlet?: string | null;
  horizonWindow?: string | null;
  hotelView?: HotelTab;
  assistantPane?: 'notebook' | 'dashboard';
  /** Mark the URL as carrying the whole view context (`vc=1`). */
  viewContextMarked?: boolean;
};

export type OpsUrlSyncSetters = {
  setRegionFilter: (r: string | null) => void;
  setCompareItems: (items: ComparisonItem[]) => void;
  setFocusOutlet: (o: Outlet | null) => void;
};

type UseOpsUrlSyncArgs = {
  restaurantId: string;
  state: OpsUrlSyncState;
  scopeGroups: ScopeGroupSummary[];
  fleetRestaurants?: FleetRestaurant[];
  /** True once the accessible-restaurant catalog has loaded (may be empty). */
  outletCatalogReady?: boolean;
  /** True once getScopeConfig has loaded manual groups. */
  scopeGroupsReady?: boolean;
  outlets: Outlet[];
  setters: OpsUrlSyncSetters;
  pendingFocusOutletId: string | null;
};

function isValidScopeFilter(
  filter: string,
  scopeGroups: ScopeGroupSummary[],
  fleetRestaurants?: FleetRestaurant[],
): boolean {
  if (!filter || filter === 'all' || isOrgFilter(filter)) return true;
  // A set of venues is valid while it names one; the server keeps only the
  // ones the person can open.
  if (isVenuesFilter(filter)) return venueIdsFromFilter(filter).length > 0;

  const groupId = scopeGroupIdFromFilter(filter);
  if (groupId) {
    return scopeGroups.some(g => g.id === groupId);
  }

  if (isOutletFilter(filter)) {
    if (!fleetRestaurants) return true;
    return fleetRestaurants.some(r => `outlet:${r.id}` === filter);
  }

  return true;
}

function filterNeedsOutletCatalog(filter: string | null | undefined): boolean {
  return !!filter && isOutletFilter(filter);
}

function filterNeedsGroupCatalog(filter: string | null | undefined): boolean {
  return !!scopeGroupIdFromFilter(filter);
}

/**
 * Keeps the browser URL in sync with OpsCommand view state (debounced), and
 * validates scope/compare/focus ids once the fleet catalog is available.
 */
export function useOpsUrlSync({
  restaurantId,
  state,
  scopeGroups,
  fleetRestaurants,
  outletCatalogReady = false,
  scopeGroupsReady = false,
  outlets,
  setters,
  pendingFocusOutletId,
}: UseOpsUrlSyncArgs): void {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const validatedRef = useRef(false);
  const focusDismissedRef = useRef(false);
  const prevFocusOutletIdRef = useRef<string | null>(null);

  const writeUrl = (focusOutletId: string | null) => {
    const urlState = opsStateFromView({
      mode: state.mode,
      regionFilter: state.regionFilter,
      scopeVenueIds: state.scopeVenueIds,
      compareItems: state.compareItems,
      source: state.source,
      status: state.status,
      shifts: state.shifts,
      perfSettings: state.perfSettings,
      drillKey: state.drillKey,
      perfDrill: state.perfDrill,
      focusOutletId,
      northStarDrill: state.northStarDrill,
      growthView: state.growthView,
      growthDive: state.growthDive,
      growthFacet: state.growthFacet,
      growthChannel: state.growthChannel,
      growthModel: state.growthModel,
      growthDemo: state.growthDemo,
      horizonDate: state.horizonDate,
      horizonOutlet: state.horizonOutlet,
      horizonWindow: state.horizonWindow,
      hotelView: state.hotelView,
      assistantPane: state.assistantPane,
      viewContextMarked: state.viewContextMarked,
    });
    replaceOpsUrl(restaurantId, urlState);
  };

  useEffect(() => {
    const focusId = state.focusOutlet?.id ?? null;
    const prevFocusId = prevFocusOutletIdRef.current;
    prevFocusOutletIdRef.current = focusId;

    if (focusId) {
      focusDismissedRef.current = false;
      return;
    }

    if (prevFocusId) {
      // Operator closed the drawer — drop focus from the URL immediately so
      // hydration logic cannot reopen it while the debounced sync catches up.
      focusDismissedRef.current = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      writeUrl(null);
    }
  }, [
    restaurantId,
    state.focusOutlet,
    state.mode,
    state.regionFilter,
    state.compareItems,
    state.source,
    state.status,
    state.shifts,
    state.perfSettings,
    state.drillKey,
    state.perfDrill,
    state.northStarDrill,
  ]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      writeUrl(state.focusOutlet?.id ?? null);
    }, 100);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [
    restaurantId,
    state.mode,
    state.regionFilter,
    state.scopeVenueIds?.join(','),
    state.compareItems,
    state.source,
    state.status,
    state.shifts,
    state.perfSettings,
    state.drillKey,
    state.perfDrill,
    state.focusOutlet,
    state.northStarDrill,
    state.growthView,
    state.growthDive,
    state.growthFacet,
    state.growthChannel,
    state.growthModel,
    state.growthDemo,
    state.horizonDate,
    state.horizonOutlet,
    state.horizonWindow,
    state.hotelView,
    state.assistantPane,
  ]);

  useEffect(() => {
    if (validatedRef.current) return;

    const filtersToCheck = [
      state.regionFilter,
      ...state.compareItems.map(item => item.filter),
    ];
    const needsOutlets = filtersToCheck.some(filterNeedsOutletCatalog);
    const needsGroups = filtersToCheck.some(filterNeedsGroupCatalog);
    // Empty [] used to be treated as "catalog loaded" and wiped restored
    // group:/outlet: filters before listAccessibleRestaurants / getScopeConfig
    // returned. Wait until the catalogs needed for the active filter are ready.
    if (needsOutlets && !outletCatalogReady) return;
    if (needsGroups && !scopeGroupsReady) return;

    const { setRegionFilter, setCompareItems } = setters;
    const catalog = fleetRestaurants ?? [];

    if (state.compareItems.length >= 2) {
      const sanitized = state.compareItems.filter(item =>
        isValidScopeFilter(item.filter, scopeGroups, catalog),
      );
      if (sanitized.length !== state.compareItems.length) {
        setCompareItems(
          sanitized.length >= 2
            ? compareItemsFromFilters(
                sanitized.map(i => i.filter),
                scopeGroups,
                catalog,
              )
            : [],
        );
        if (sanitized.length < 2) {
          setRegionFilter('all');
        }
      }
    } else if (
      state.regionFilter &&
      state.regionFilter !== 'all' &&
      !isValidScopeFilter(state.regionFilter, scopeGroups, catalog)
    ) {
      setRegionFilter('all');
    }

    validatedRef.current = true;
  }, [
    fleetRestaurants,
    outletCatalogReady,
    scopeGroupsReady,
    scopeGroups,
    setters,
    state.compareItems,
    state.regionFilter,
  ]);

  useEffect(() => {
    if (!pendingFocusOutletId || state.focusOutlet || focusDismissedRef.current) {
      return;
    }
    if (!outletCatalogReady || !fleetRestaurants?.length) return;

    const fleetMatch = fleetRestaurants.find(r => r.id === pendingFocusOutletId);
    if (!fleetMatch) return;

    const outlet = outlets.find(o => o.id === pendingFocusOutletId);
    if (outlet) {
      setters.setFocusOutlet(outlet);
    }
  }, [
    pendingFocusOutletId,
    state.focusOutlet,
    fleetRestaurants,
    outletCatalogReady,
    outlets,
    setters,
  ]);
}
